<template>
  <div class="top-nav">
    <div class="nav-grid">
      <div class="nav-section settings-section">
        <button type="button" class="icon-btn icon-btn-sm" title="How it works" aria-label="How it works" @click="$emit('show-tutorial')">
          <i class="fas fa-question-circle"></i>
        </button>
        <button type="button" class="icon-btn icon-btn-sm" title="Settings" aria-label="Settings" @click="$emit('show-settings')">
          <i class="fas fa-cog"></i>
        </button>
        <button type="button" class="icon-btn icon-btn-sm" title="Feedback" aria-label="Send feedback" @click="$emit('show-feedback')">
          <i class="fas fa-comment-dots"></i>
        </button>
      </div>
      
      <div class="nav-section clock-section">
        <span class="nav-text time-text">
          <i class="fas fa-clock"></i>{{ timeRange }}
        </span>

        <span v-if="sentenceCount > 0" class="nav-text sentence-text" title="Sentence progress">
          <i class="fas fa-list-ol"></i>{{ sentenceIndex + 1 }}/{{ sentenceCount }}
        </span>
      </div>

      <div class="nav-section controls-section">
        <div class="status-indicators">
          <button type="button" class="nav-text practice-text" title="Your practice" aria-label="Your practice" @click="goToStats">
            <i class="fas fa-chart-bar"></i>{{ todayPracticeLabel }}
          </button>

          <!-- Tap cycles 1x -> 0.75x -> 0.5x -->
          <button
            type="button"
            class="icon-btn icon-btn-sm speed-control"
            :class="{ 'is-active': playbackRate !== 1 }"
            title="Playback speed"
            aria-label="Playback speed"
            @click="cyclePlaybackRate"
          >{{ playbackRateLabel }}</button>

          <!-- Not in the action bar: that bar owns drill actions only. -->
          <button
            type="button"
            class="icon-btn icon-btn-sm"
            title="Share this sentence"
            aria-label="Share this sentence"
            @click="$emit('show-share')"
          >
            <i class="fas fa-share-alt"></i>
          </button>

          <button
            type="button"
            class="icon-btn icon-btn-sm microphone-control"
            :class="{
              'is-active': speakingMode,
              'listening': isRecognitionActive,
              'speaking': voiceDetected
            }"
            :title="speakingMode ? 'Turn microphone off' : 'Turn microphone on'"
            :aria-label="speakingMode ? 'Turn microphone off' : 'Turn microphone on'"
            :aria-pressed="speakingMode ? 'true' : 'false'"
            @click="$emit('toggle-speech-mode')"
          >
            <i class="fas" :class="speakingMode ? 'fa-microphone' : 'fa-microphone-slash'"></i>
          </button>

        </div>
      </div>
    </div>

    <div v-if="sentenceCount > 0" class="sentence-progress-track">
      <div class="sentence-progress-track__fill" :style="{ width: sentenceProgressPercent + '%' }"></div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'TopNavigation',
  props: {
    currentStartTime: {
      type: Number,
      default: 0
    },
    currentEndTime: {
      type: Number,
      default: 0
    },
    sentenceIndex: {
      type: Number,
      default: 0
    },
    sentenceCount: {
      type: Number,
      default: 0
    }
  },
  computed: {
    // Read reactively so the mic indicator responds to voice instantly.
    ...mapState('recognition', {
      speakingMode: state => state.speakingMode,
      isRecognitionActive: state => state.micActive,
      voiceDetected: state => state.voiceDetected
    }),
    ...mapState('player', ['playbackRate']),
    ...mapState('stats', { todayPracticeSeconds: state => state.todaySeconds }),

    timeRange() {
      return this.formatTime(this.currentStartTime) + ' - ' + this.formatTime(this.currentEndTime);
    },
    sentenceProgressPercent() {
      if (!this.sentenceCount) return 0;
      return Math.min(100, (this.sentenceIndex / this.sentenceCount) * 100);
    },
    playbackRateLabel() {
      // Strip the leading zero: "0.75" -> ".75".
      return this.playbackRate === 1
        ? '1x'
        : String(this.playbackRate).replace(/^0\./, '.') + 'x';
    },
    todayPracticeLabel() {
      const total = Math.floor(this.todayPracticeSeconds);
      if (total >= 3600) {
        const h = Math.floor(total / 3600);
        const m = Math.floor((total % 3600) / 60);
        return `${h}h ${String(m).padStart(2, '0')}m`;
      }
      const m = Math.floor(total / 60);
      const s = total % 60;
      return `${m}:${String(s).padStart(2, '0')}`;
    }
  },
  methods: {
    formatTime(time) {
      const hours = Math.floor(time / 3600).toString().padStart(2, '0');
      const minutes = Math.floor((time % 3600) / 60).toString().padStart(2, '0');
      const seconds = Math.floor(time % 60).toString().padStart(2, '0');
      // No "00:" hours prefix: at 360px the row has no room for it (STORY-016 added a control).
      return hours === '00' ? `${minutes}:${seconds}` : `${hours}:${minutes}:${seconds}`;
    },
    
    cyclePlaybackRate() {
      this.$store.dispatch('player/cyclePlaybackRate');
    },

    goToStats() {
      if (this.$route && this.$route.path !== '/stats') {
        this.$router.push('/stats');
      }
    }
  }
}
</script>

<style scoped>
.top-nav {
  background: var(--chrome);
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2; /* Sit above the video below it */
}

.nav-grid {
  display: flex;
  align-items: center;
  width: 100%;
  height: 40px;
  padding: 0 6px;
  box-sizing: border-box;
  gap: 4px;
}

.nav-section {
  display: flex;
  align-items: center;
  gap: 2px;
}

.settings-section,
.controls-section {
  flex: none;
}

.clock-section {
  flex: 1;
  justify-content: center;
  gap: 14px;
  min-width: 0;
}

.status-indicators {
  display: flex;
  align-items: center;
  gap: 2px;
}

.nav-text {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--muted);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.nav-text i {
  font-size: 10px;
  color: var(--faint);
}

.time-text {
  color: var(--text);
}

/* Text button: same 32px height and radius family as the icon buttons. */
.practice-text {
  height: 32px;
  padding: 0 10px;
  border-radius: var(--r-pill);
  transition: background-color 0.15s ease, color 0.15s ease;
}

.practice-text:hover {
  color: var(--text);
  background: var(--field);
}

.speed-control {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.microphone-control.listening {
  background: var(--accent-soft);
}

.microphone-control.speaking {
  background: var(--pass);
  color: var(--pass-ink);
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0%   { box-shadow: 0 0 0 0 rgba(63, 191, 127, 0.5); }
  70%  { box-shadow: 0 0 0 6px rgba(63, 191, 127, 0); }
  100% { box-shadow: 0 0 0 0 rgba(63, 191, 127, 0); }
}

/* Doubles as the boundary between header and video. */
.sentence-progress-track {
  width: 100%;
  height: 2px;
  background: var(--line);
  overflow: hidden;
}

.sentence-progress-track__fill {
  height: 100%;
  background: var(--accent);
  transition: width 0.3s ease;
}

@media (max-width: 576px) {
  .clock-section {
    gap: 8px;
  }

  .nav-text {
    font-size: 11px;
  }

  .practice-text {
    padding: 0 6px;
  }

  .sentence-text i {
    display: none;
  }
}

/* The dense row: 6 controls + clock must fit 360px. */
@media (max-width: 400px) {
  .icon-btn-sm { width: 28px; height: 28px; min-width: 28px; }
  .clock-section { gap: 4px; }
  .time-text i { display: none; }
}

@media (prefers-reduced-motion: reduce) {
  .microphone-control.speaking { animation: none; }
}
</style>
